import { useState } from 'react';
import { X, HelpCircle, Smartphone, Monitor, MessageSquare, ChevronRight, Star } from 'lucide-react';

const steps = [
  {
    platform: 'SMS suspect',
    icon: MessageSquare,
    steps: [
      'Ouvrez le SMS suspect',
      'Appuyez longtemps sur le texte du message',
      'Sélectionnez tout et copiez',
      'Collez-le ici — on vérifie tout pour vous',
    ],
  },
  {
    platform: 'Gmail (téléphone)',
    icon: Smartphone,
    steps: [
      'Ouvrez le mail suspect',
      'Appuyez sur les 3 points en haut à droite (⋮)',
      'Appuyez sur "Transférer"',
      'Sélectionnez tout le texte et copiez-le',
      'Collez-le ici',
    ],
  },
  {
    platform: 'Gmail (ordinateur)',
    icon: Monitor,
    steps: [
      'Ouvrez le mail suspect',
      'Cliquez sur les 3 points en haut à droite du mail',
      'Cliquez sur "Afficher l\'original"',
      'Copiez tout le contenu affiché (Ctrl+A puis Ctrl+C)',
      'Collez-le ici',
    ],
    bonus: true,
  },
  {
    platform: 'Yahoo / Outlook',
    icon: Monitor,
    steps: [
      'Ouvrez le mail suspect',
      'Sélectionnez tout le contenu visible (texte, liens, etc.)',
      'Copiez-le (Ctrl+C ou clic droit > Copier)',
      'Collez-le ici',
    ],
  },
  {
    platform: 'WhatsApp',
    icon: MessageSquare,
    steps: [
      'Ouvrez le message suspect',
      'Appuyez longtemps sur le message',
      'Appuyez sur "Copier"',
      'Collez-le ici',
    ],
  },
];

export default function EmailGuideOverlay({ onClose }) {
  const [activeTab, setActiveTab] = useState(0);
  const current = steps[activeTab];
  const Icon = current.icon;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      <div
        className="relative bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl max-h-[85vh] overflow-y-auto shadow-2xl animate-slide-up"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-100 px-5 py-4 flex items-center justify-between rounded-t-2xl z-10">
          <div className="flex items-center gap-2">
            <HelpCircle className="w-5 h-5 text-accent-blue" />
            <h2 className="font-bold text-brand-navy text-lg">Comment ça marche ?</h2>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center active:scale-90 transition-transform">
            <X className="w-4 h-4 text-gray-500" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 px-4 pt-4 overflow-x-auto scrollbar-hide">
          {steps.map((s, i) => (
            <button
              key={i}
              onClick={() => setActiveTab(i)}
              className={`px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${
                i === activeTab
                  ? 'bg-accent-blue text-white'
                  : 'bg-gray-100 text-gray-600 active:bg-gray-200'
              }`}
            >
              {s.platform}
            </button>
          ))}
        </div>

        {/* Steps */}
        <div className="px-5 py-4 space-y-3">
          <div className="flex items-center gap-2 text-brand-navy font-semibold">
            <Icon className="w-5 h-5" />
            <span>{current.platform}</span>
          </div>

          <ol className="space-y-2">
            {current.steps.map((step, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="w-6 h-6 bg-accent-blue/10 text-accent-blue rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">
                  {i + 1}
                </span>
                <span className="text-sm text-text-primary leading-relaxed">{step}</span>
              </li>
            ))}
          </ol>

          {/* Bonus pour "Afficher l'original" */}
          {current.bonus && (
            <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-xl">
              <div className="flex items-center gap-1.5 mb-1">
                <Star className="w-3.5 h-3.5 text-accent-blue" />
                <p className="text-xs font-bold text-accent-blue">Analyse renforcée</p>
              </div>
              <p className="text-xs text-blue-800 leading-relaxed">
                Avec "Afficher l'original", on reçoit les données techniques complètes du mail.
                Cela permet de vérifier si le serveur d'envoi est autorisé et si le mail a été modifié en route.
                Résultat : une analyse <strong>beaucoup plus précise</strong>.
              </p>
            </div>
          )}
        </div>

        {/* Tip */}
        <div className="mx-5 mb-5 p-3 bg-green-50 border border-green-200 rounded-xl">
          <p className="text-xs text-green-800 leading-relaxed">
            <strong>💡 Ça marche avec tout :</strong> emails, SMS, messages WhatsApp, messages Facebook... Copiez le texte suspect et collez-le ici. On analyse le contenu, les liens et les techniques d'arnaque.
          </p>
        </div>

        {/* CTA */}
        <div className="sticky bottom-0 bg-white border-t border-gray-100 px-5 py-4">
          <button
            onClick={onClose}
            className="w-full btn-primary"
          >
            J'ai compris
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
